import React from "react";
import { Accordion, AccordionDetails, AccordionSummary, CircularProgress } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { SovosBox, SovosButton, SovosTypography } from "mosaic-react";
import { KPI_BORDER_COLORS } from "../../constants";
import StatTile from "../../components/StatTile";

const FILTER_ALL = "all";
const FILTER_ERRORS = "errors";
const FILTER_WARNINGS = "warnings";

const ISSUE_LABELS = {
    TYPE_MISMATCH: "Type mismatch — value cannot be stored in the target field type",
    MANDATORY_MISSING: "Missing mandatory value",
    VALUE_OUT_OF_RANGE: "Value exceeds the target field length",
};

const IssuesSummaryPanel = ({
    activeFilter,
    affectedRows,
    isRecalculating,
    isScanning,
    issueGroups,
    issuesData,
    onFilterChange,
    totalErrors,
    totalWarnings,
}) => {
    return (
        <>
            <SovosBox sx={{ display: "flex", gap: 1.5, mb: 2, alignItems: "flex-end" }}>
                {isScanning ? (
                    <SovosBox sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
                        <CircularProgress size={14} />
                        <SovosTypography variant="caption" sx={{ color: "text.secondary" }}>
                            Scanning for issues…
                        </SovosTypography>
                    </SovosBox>
                ) : (
                    <>
                        <StatTile label="Errors" value={totalErrors} borderColor="error.main" />
                        <StatTile label="Warnings" value={totalWarnings} borderColor="warning.main" />
                        <StatTile label="Affected rows" value={affectedRows} borderColor={KPI_BORDER_COLORS[2]} />
                        {isRecalculating && (
                            <SovosBox sx={{ display: "flex", alignItems: "center", gap: 0.75, pb: 0.25 }}>
                                <CircularProgress size={12} />
                                <SovosTypography variant="caption" sx={{ color: "text.secondary" }}>
                                    Recalculating…
                                </SovosTypography>
                            </SovosBox>
                        )}
                    </>
                )}
            </SovosBox>

            {issuesData && (totalErrors > 0 || totalWarnings > 0) && (
                <Accordion
                    disableGutters
                    elevation={0}
                    sx={{
                        mb: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: "6px !important",
                        "&:before": { display: "none" },
                    }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={{ minHeight: 40, px: 1.5, py: 0 }}>
                        <SovosTypography variant="caption" fontWeight={600}>
                            Issue summary — {totalErrors} error{totalErrors !== 1 ? "s" : ""}, {totalWarnings} warning{totalWarnings !== 1 ? "s" : ""}
                        </SovosTypography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.5 }}>
                        {issueGroups.errors.length > 0 && (
                            <SovosBox sx={{ mb: issueGroups.warnings.length > 0 ? 1 : 0 }}>
                                <SovosTypography variant="caption" fontWeight={700} sx={{ color: "error.main", display: "block", mb: 0.5 }}>
                                    Errors
                                </SovosTypography>
                                {issueGroups.errors.map((g) => (
                                    <SovosBox key={`${g.column}-${g.type}`} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.25 }}>
                                        <SovosBox
                                            sx={{
                                                width: 6,
                                                height: 6,
                                                borderRadius: "50%",
                                                bgcolor: "error.main",
                                                flexShrink: 0,
                                            }}
                                        />
                                        <SovosTypography variant="caption" fontWeight={600} sx={{ minWidth: 120 }}>
                                            {g.column}
                                        </SovosTypography>
                                        <SovosTypography variant="caption" sx={{ color: "text.secondary" }}>
                                            {ISSUE_LABELS[g.type] || g.type}
                                        </SovosTypography>
                                        <SovosTypography variant="caption" sx={{ color: "error.main", ml: "auto", fontWeight: 600 }}>
                                            {g.count} row{g.count !== 1 ? "s" : ""}
                                        </SovosTypography>
                                    </SovosBox>
                                ))}
                            </SovosBox>
                        )}
                        {issueGroups.warnings.length > 0 && (
                            <SovosBox>
                                <SovosTypography variant="caption" fontWeight={700} sx={{ color: "warning.dark", display: "block", mb: 0.5 }}>
                                    Warnings
                                </SovosTypography>
                                {issueGroups.warnings.map((g) => (
                                    <SovosBox key={`${g.column}-${g.type}`} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.25 }}>
                                        <SovosBox
                                            sx={{
                                                width: 6,
                                                height: 6,
                                                borderRadius: "50%",
                                                bgcolor: "warning.main",
                                                flexShrink: 0,
                                            }}
                                        />
                                        <SovosTypography variant="caption" fontWeight={600} sx={{ minWidth: 120 }}>
                                            {g.column}
                                        </SovosTypography>
                                        <SovosTypography variant="caption" sx={{ color: "text.secondary" }}>
                                            {ISSUE_LABELS[g.type] || g.type}
                                        </SovosTypography>
                                        <SovosTypography variant="caption" sx={{ color: "warning.dark", ml: "auto", fontWeight: 600 }}>
                                            {g.count} row{g.count !== 1 ? "s" : ""}
                                        </SovosTypography>
                                    </SovosBox>
                                ))}
                            </SovosBox>
                        )}
                    </AccordionDetails>
                </Accordion>
            )}

            {issuesData && (totalErrors > 0 || totalWarnings > 0) && (
                <SovosBox sx={{ display: "flex", gap: 1, mb: 2, alignItems: "center", flexWrap: "wrap" }}>
                    <SovosButton
                        size="small"
                        variant={activeFilter === FILTER_ALL ? "contained" : "outlined"}
                        onClick={() => onFilterChange(FILTER_ALL)}
                    >
                        All
                    </SovosButton>
                    {totalErrors > 0 && (
                        <SovosButton
                            size="small"
                            variant={activeFilter === FILTER_ERRORS ? "contained" : "outlined"}
                            color="error"
                            onClick={() => onFilterChange(FILTER_ERRORS)}
                        >
                            {`Errors (${totalErrors})`}
                        </SovosButton>
                    )}
                    {totalWarnings > 0 && (
                        <SovosButton
                            size="small"
                            variant={activeFilter === FILTER_WARNINGS ? "contained" : "outlined"}
                            onClick={() => onFilterChange(FILTER_WARNINGS)}
                        >
                            {`Warnings (${totalWarnings})`}
                        </SovosButton>
                    )}
                </SovosBox>
            )}
        </>
    );
};

export default IssuesSummaryPanel;
